import PropTypes from 'prop-types';
import { useEffect, useState } from 'react';
import { useRouter } from 'next/router';

import { getContent, isDDEnv } from '../../../utils/helper';
import {
  analytics,
  getPrintDetail,
  getVisitWebsite,
  getDirections,
} from '../../../utils/analytics';

import BottomSheet from '../BottomSheet/BottomSheet';
import Button from '../../atoms/Button/Button';
import ClipAnimation from '../../atoms/ClipAnimation/ClipAnimation';
import { CtaWrapper } from '../../molecules/CTA';
import { DynamicAsset } from '../../molecules/Media/Media';
import Icon from '../../atoms/Icon/Icon';
import Pill from '../../atoms/Pill/Pill';
import SocialShare from '../../molecules/SocialShare/SocialShare';
import Text from '../../atoms/Text/Text';

function HeaderButton({
  canStartAnimation,
  onClick,
  iconName,
  label,
  hasRightBorder,
  wrapperStyle = '',
}) {
  return (
    <div className={`flex ${wrapperStyle}`}>
      <ClipAnimation startAnimation={canStartAnimation}>
        <Button style="aaco-header-button flex items-center" onClick={onClick}>
          <Icon
            className="mr-3.5 text-white"
            height={18}
            name={iconName}
            width={18}
          />
          <Text tag="button" style="text-white">
            {label}
          </Text>
        </Button>
      </ClipAnimation>
      {hasRightBorder && (
        <div className="border-l-1 border-white ml-4 md:mx-4" />
      )}
    </div>
  );
}

function RecipeHeaderDD({ assets = [], entries = [], fields = {} }) {
  const [open, setOpen] = useState(false);
  const [pageUrl, setPageUrl] = useState('');
  const [canStartAnimation, setCanStartAnimation] = useState(false);

  const router = useRouter();
  const isKorean = router?.locale === 'ko-KR';

  const openWindowPrint = (e) => {
    analytics({
      window,
      document,
      detail: getPrintDetail(e),
    });
    window?.print();
  };
  const openDirection = (e) => {
    analytics({
      window,
      document,
      detail: getDirections(e, {
        url: fields?.mapLink,
      }),
    });
    window?.open(fields?.mapLink, '_blank');
  };
  const openWebsite = (e) => {
    analytics({
      window,
      document,
      detail: getVisitWebsite(e, {
        url: fields?.websiteLink,
      }),
    });
    window?.open(fields?.websiteLink, '_blank');
  };
  const openShare = () => setOpen(true);
  const closeShare = () => setOpen(false);

  const {
    title,
    asset,
    featuredTags,
    short,
    servings,
    maxServings,
    servingsName,
    time,
    cloudinaryAsset,
    shortLine2,
    websiteLink,
    mapLink,
  } = fields;

  useEffect(() => {
    setCanStartAnimation(true);

    return () => setCanStartAnimation(false);
  }, []);

  useEffect(() => {
    if (window?.location) {
      setPageUrl(window.location.href);
    }
  }, []);

  const mapIdx = mapLink ? 1 : 0;
  const webIdx = websiteLink ? mapIdx + 1 : 0;
  const printIdx = webIdx + 1 ?? 0;
  const shareIdx = printIdx + 1;

  return (
    <>
      <div className="shown-print aaco-header flex flex-col">
        <div className="aaco-header__image-desktop w-full">
          <DynamicAsset field={asset} assets={assets} entries={entries} />
        </div>
        <Text tag="h3 mt-5">{title?.replace(/-/gm, '-\u2060')}</Text>
        <Text style="mt-3.5" tag="tag">
          {`${servings ?? 1} ${maxServings ? `- ${maxServings} ` : ''}${
            servingsName ? servingsName : `Serving${servings > 0 ? 's' : ''}`
          }${time ? ` • ${time}` : ''} ${short ? `• ${short}` : ''}`}
        </Text>
      </div>
      <div
        className="hidden-print aaco-header aaco-recipe-header mb-20 h-[calc(100vh-5rem)] flex column justify-center items-center dd-kr:items-end dd-en:items-end"
        aria-label={title}
      >
        <div className="relative w-full h-full">
          <div className="aaco-header__image-desktop h-full">
            <DynamicAsset
              field={cloudinaryAsset ?? asset}
              assets={assets}
              entries={entries}
            />
          </div>
          <div className="aaco-header__bg-shadow absolute top-0 right-0 bottom-0 left-0 z-10 h-full w-full" />
        </div>
        <div className="aaco-header__content h-max absolute w-full md:w-1/2 text-center align-center z-20 px-5  dd-kr:mb-[62px] dd-en:mb-[62px]">
          <ClipAnimation startAnimation={canStartAnimation} fullWidth>
            <div className="aaco-header__featured-tags flex flex-wrap justify-center gap-4 mb-6 md:mb-5 dd-en:mb-4 dd-kr:mb-4 dd-en:md:mb-5 dd-kr:md:mb-5">
              {featuredTags?.length > 0 &&
                featuredTags.map((t) => {
                  const tag = getContent(t?.sys?.id, entries)?.fields;
                  return (
                    <CtaWrapper
                      classNames="justify-center"
                      key={tag?.name}
                      url={`/search?t=${tag?.slug}`}
                    >
                      <Pill style={isDDEnv() ? 'pill-action' : ''} invert>
                        {tag?.name}
                      </Pill>
                    </CtaWrapper>
                  );
                })}
            </div>
          </ClipAnimation>

          {title && (
            <ClipAnimation startAnimation={canStartAnimation} fullWidth>
              <Text tag="h3" style="text-white pb-2 dd-en:pb-0 dd-kr:pb-0">
                {title?.replace(/-/gm, '-\u2060')}
              </Text>
            </ClipAnimation>
          )}
          <div className="aaco-header__subtitle dd-en:hidden dd-kr:hidden flex justify-center mt-24 md:mt-0">
            <ClipAnimation startAnimation={canStartAnimation}>
              <Text style="mt-3.5	text-white" tag="tag">
                {(servings
                  ? `${servings ?? 1} ${
                      maxServings ? `- ${maxServings} ` : ''
                    }${
                      servingsName
                        ? servingsName
                        : `Serving${servings > 0 ? 's' : ''}`
                    }${time ? ` • ${time}` : ''} • `
                  : '') + `${short}`}
              </Text>
              {shortLine2 && (
                <div>
                  <Text style="mt-3.5	text-white" tag="tag">
                    {shortLine2}
                  </Text>
                </div>
              )}
            </ClipAnimation>
          </div>
          <div className="aaco-header__cta md:hidden md:container md:mx-auto grid grid-cols-2 justify-center mt-6 dd-en:mt-[30px] dd-kr:mt-[30px] gap-y-[26px]">
            {mapLink && (
              <HeaderButton
                canStartAnimation={canStartAnimation}
                onClick={openDirection}
                iconName="map"
                label="Directions"
                wrapperStyle="justify-end"
                hasRightBorder
              />
            )}
            {websiteLink && (
              <HeaderButton
                canStartAnimation={canStartAnimation}
                onClick={openWebsite}
                iconName="web"
                label="Visit Website"
                wrapperStyle={
                  webIdx % 2 === 0 ? 'justify-start ml-4' : 'justify-end'
                }
              />
            )}
            <HeaderButton
              canStartAnimation={canStartAnimation}
              onClick={openWindowPrint}
              iconName="print"
              label={isKorean ? '프린트' : 'Print'}
              wrapperStyle={
                printIdx % 2 === 0 ? 'justify-start  ml-4' : 'justify-end'
              }
              hasRightBorder
            />
            <HeaderButton
              canStartAnimation={canStartAnimation}
              onClick={openShare}
              iconName="share"
              label={isKorean ? '공유' : 'Share'}
              wrapperStyle={
                shareIdx % 2 === 0 ? 'justify-start  ml-4' : 'justify-end'
              }
            />
          </div>
          <div className="hidden md:flex aaco-header__cta justify-center mt-14 dd-kr:md:mt-[30px] dd-en:md:mt-[30px]">
            {mapLink && (
              <HeaderButton
                canStartAnimation={canStartAnimation}
                onClick={openDirection}
                iconName="map"
                label="Directions"
                hasRightBorder
              />
            )}
            {websiteLink && (
              <HeaderButton
                canStartAnimation={canStartAnimation}
                onClick={openWebsite}
                iconName="web"
                label="Visit Website"
                hasRightBorder
              />
            )}
            <HeaderButton
              canStartAnimation={canStartAnimation}
              onClick={openWindowPrint}
              iconName="print"
              label={isKorean ? '프린트' : 'Print'}
              hasRightBorder
            />
            <HeaderButton
              canStartAnimation={canStartAnimation}
              onClick={openShare}
              iconName="share"
              label={isKorean ? '공유' : 'Share'}
            />
          </div>
        </div>
        <div>
          <BottomSheet open={open} onDismiss={closeShare}>
            <SocialShare url={pageUrl} size={50} title={title} />
          </BottomSheet>
        </div>
      </div>
    </>
  );
}

RecipeHeaderDD.propTypes = {
  assets: PropTypes.array,
  entries: PropTypes.array,
  fields: PropTypes.object,
};

export default RecipeHeaderDD;
